/**
 * Okta 认证工具函数
 * 实现 PKCE (Proof Key for Code Exchange) 流程
 */

import { jwtDecode } from 'jwt-decode';
import { parse, stringify } from 'qs';
import { getOktaConfig, IOktaConfig } from '../api/request/config';

// JWT Token 接口
export interface IJWT {
  sub: string;
  name?: string;
  email?: string;
  exp: number;
  iat: number;
  aud?: string;
  iss?: string;
  [key: string]: unknown;
}

// 重新导出 IOktaConfig 以便其他模块使用
export type { IOktaConfig };

/**
 * 获取页面查询参数
 */
export const getPageQuery = () => parse(window.location.href.split('?')[1]);

/**
 * 获取当前域名
 */
export const getHostname = () => window.location.origin;

/**
 * 生成随机字符串
 * @param length 字符串长度
 */
export const getRandomString = (length: number): string => {
  const a = new Uint8Array(Math.ceil(length / 2));
  crypto.getRandomValues(a);
  const str = Array.from(a, (dec) => `0${dec.toString(16)}`.substr(-2)).join('');
  return str.slice(0, length);
};

/**
 * 创建 PKCE code verifier
 * @returns code verifier 字符串
 */
export const makeVerifier = (): string => {
  const minLength = 43;
  const maxLength = 128;

  let verifier = '';

  if (verifier.length < minLength) {
    verifier = verifier + getRandomString(minLength - verifier.length);
  }

  const codeVerifier = encodeURIComponent(verifier).slice(0, maxLength);

  // 保存 code verifier 到 localStorage
  localStorage.setItem('code_verifier', codeVerifier);
  return codeVerifier;
};

/**
 * Base64 URL 编码
 * @param input 输入字符串
 */
export const base64URLEncode = (input: string): string => {
  return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * 创建 PKCE code challenge
 * @param str code verifier 字符串
 */
export const makeChallenge = async (str: string): Promise<string> => {
  const buffer = new TextEncoder().encode(str);
  const arrayBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hash = String.fromCharCode.apply(null, Array.from(new Uint8Array(arrayBuffer)));
  return base64URLEncode(hash);
};

/**
 * 从 JWT 获取用户信息
 */
export const getUserFromJWT = (): IJWT | undefined => {
  const token = localStorage.getItem('access_token');
  if (token) {
    try {
      return jwtDecode<IJWT>(token);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return undefined;
    }
  }
  return undefined;
};

/**
 * 检查 token 是否过期
 */
export const isTokenExpire = (): boolean => {
  const jwt = getUserFromJWT();
  if (jwt) {
    return new Date().getTime() > jwt.exp * 1000;
  }
  return true;
};

/**
 * 检查是否已登录
 */
export const isLoggedIn = (): boolean => {
  const token = localStorage.getItem('access_token');
  return !!token && !isTokenExpire();
};

/**
 * 跳转到登录页面
 */
export const userLoginPage = (loginPath = '/auth-pages/login'): void => {
  window.location.href = loginPath;
};

/**
 * 登录后重定向
 */
export const redirectPage = (defaultPath = '/'): void => {
  const urlParams = new URL(window.location.href);
  const params = getPageQuery();
  let { redirect } = params as { redirect: string };

  if (redirect) {
    const redirectUrlParams = new URL(redirect);
    if (redirectUrlParams.origin === urlParams.origin) {
      redirect = redirect.substr(urlParams.origin.length);
      if (redirect.match(/^\/.*#/)) {
        redirect = redirect.substr(redirect.indexOf('#') + 1);
      }
    } else {
      window.location.href = defaultPath;
      return;
    }
  }
  window.location.href = redirect || defaultPath;
};

/**
 * 跳转到 Okta 授权页面
 */
export const authorizeWithOkta = async (): Promise<void> => {
  const oktaConfig = getOktaConfig();

  if (!oktaConfig.host || !oktaConfig.clientId) {
    console.error('Okta configuration is missing');
    return;
  }

  const params = new URLSearchParams();
  params.set('client_id', oktaConfig.clientId);
  params.set('redirect_uri', `${getHostname()}${oktaConfig.redirectURI}`);
  params.set('response_type', 'code');
  params.set('state', 'OR');
  params.set('code_challenge', await makeChallenge(makeVerifier()));
  params.set('code_challenge_method', 'S256');
  params.set('scope', oktaConfig.scope || '');

  const url = `${oktaConfig.host}/v1/authorize?${params.toString()}`;
  window.location.href = url;
};

/**
 * 登出
 * @param loginPath 登录页面路径
 */
export const logout = (loginPath = '/auth-pages/login'): void => {
  const { redirect } = getPageQuery();

  // 清除 token
  localStorage.removeItem('access_token');
  localStorage.removeItem('code_verifier');

  // 跳转到登录页
  if (window.location.pathname !== loginPath && !redirect) {
    window.location.href = `${loginPath}?${stringify({
      redirect: window.location.href,
    })}`;
  }
};

/**
 * 获取 access token
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

/**
 * 设置 access token
 */
export const setAccessToken = (token: string): void => {
  localStorage.setItem('access_token', token);
};

// 重新导出 getOktaConfig 以保持向后兼容
export { getOktaConfig };
